import type { BulkClaimEditInput } from '@billing/validation';
import { Alert, Button, Divider, Group, Stack } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconInfoCircle } from '@tabler/icons-react';
import type { Claim, Provider } from 'interfaces';
import type { MRT_Row } from 'mantine-react-table';
import {
  ClaimDetailSection,
  ClaimFormProvider,
  ClaimInfoSection,
  ClaimProviderSection,
  ClaimCommentSection,
  claimCommentSectionKeys,
  type AllKeys,
} from 'pages/claims/components';
import type { ClaimTableRow } from 'pages/claims/utils/claimTableRows';
import { useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { nowISO, parseDateTime } from 'utils/dateUtils';
import {
  BulkEditClaimForm,
  tariffSectionKeys,
  ClaimTariffSection,
} from './ModalComponents';

export type BulkClaimEditPayload = BulkClaimEditInput;

interface ClaimBulkEditModalProps {
  claimIDs: string[];
  claims: Claim[];
  shouldDefaultServiceTimes: boolean;
  siteTimezone: string;
  siteID: string;
  row: MRT_Row<ClaimTableRow>;
  internalEditComponents: ReactNode[];
  providers: Provider[];
  onClose: () => void;
  onConfirm: (payload: BulkClaimEditPayload) => Promise<void> | void;
}

const infoKeys: AllKeys[] = [
  'serviceStart',
  'serviceEnd',
  'payorID',
  'intakeItemID',
];
const providerKeys: AllKeys[] = ['providerID', 'groupID'];
const detailKeys: AllKeys[] = [
  'locationOfService',
  'serviceLocationIndicator',
  'hospitalCode',
  'patientCount',
  'virtualPhysicianRegion',
  'thirdPartyLiability',
  'optometristReason',
  'incorporated',
  'interpretingRadiologist',
  'chiroService',
  'facilityNumber',
];
const notesKeys: AllKeys[] = [
  'providerNotes',
  'providerComments',
  'clerkComments',
];

const getSharedPayorValues = (claims: Claim[]) => {
  const firstClaim = claims[0];
  if (
    !firstClaim?.payorID ||
    claims.some((claim) => claim.payorID !== firstClaim.payorID)
  ) {
    return {};
  }

  return {
    payorID: firstClaim.payorID,
    payor: firstClaim.payor,
  };
};

const serviceTimeKeys = new Set(['serviceStart', 'serviceEnd']);

const getChangedValues = (
  values: Record<string, unknown>,
  initialValues: Record<string, unknown>,
  shouldApplyInitialServiceTimes: boolean
) =>
  Object.entries(values).filter(([key, value]) => {
    if (key === 'payor' || value === undefined) return false;
    if (shouldApplyInitialServiceTimes && serviceTimeKeys.has(key)) return true;

    return value !== initialValues[key];
  });

export const ClaimBulkEditModal = ({
  claimIDs,
  claims,
  shouldDefaultServiceTimes,
  siteTimezone,
  siteID,
  row,
  internalEditComponents,
  providers,
  onClose,
  onConfirm,
}: ClaimBulkEditModalProps) => {
  const { translate } = useTranslation();
  const [loading, setLoading] = useState(false);
  const initialValues = useMemo<BulkClaimEditInput['values']>(() => {
    const values = getSharedPayorValues(claims);
    if (!shouldDefaultServiceTimes) return values;

    const serviceStart = parseDateTime(nowISO(siteTimezone), {
      zone: siteTimezone,
    });

    values['serviceStart'] = serviceStart.toISO();
    values['serviceEnd'] = serviceStart.plus({ minutes: 15 }).toISO();

    return values;
  }, [claims, shouldDefaultServiceTimes, siteTimezone]);
  const [hasChangedValues, setHasChangedValues] = useState(
    shouldDefaultServiceTimes
  );
  const form = BulkEditClaimForm({
    claimIDs,
    initialValues,
    onValuesChange: (values) => {
      setHasChangedValues(
        getChangedValues(
          values.values,
          initialValues,
          shouldDefaultServiceTimes
        ).length > 0
      );
    },
  });

  const valuesPath = 'values';

  const close = () => {
    if (loading) return;
    form.reset();
    setHasChangedValues(false);
    onClose();
  };

  const editComponentsMap = useMemo(() => {
    const map: Record<string, ReactNode> = {};
    internalEditComponents.forEach((comp: ReactElement) => {
      if (comp.key) map[comp.key as string] = comp;
    });
    return map;
  }, [internalEditComponents]);

  const handleConfirm = async () => {
    const { claimIDs, values } = form.getValues();
    const claimData = Object.fromEntries(
      getChangedValues(values, initialValues, shouldDefaultServiceTimes)
    );

    setLoading(true);
    try {
      await onConfirm({
        claimIDs,
        values: claimData,
      });
      form.reset();
      setHasChangedValues(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack gap="md">
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        {translate('pages:claims.bulkEdit.description', {
          count: claimIDs.length,
          defaultValue:
            'Only fields you set here will be applied to {{count}} selected claims. Existing values in all other fields will be preserved.',
        })}
      </Alert>
      <ClaimFormProvider
        form={{
          formType: 'bulkEdit',
          bulkEditForm: form,
          prefix: valuesPath,
          siteID,
        }}
      >
        <Stack gap="md">
          <ClaimInfoSection
            index={row.id}
            title={translate(
              'pages:claims.bulkEdit.billing',
              'Billing details'
            )}
            keys={infoKeys}
            cols={3}
            editComponentsMap={editComponentsMap}
          />
          <ClaimProviderSection
            index={row.id}
            title={translate(
              'pages:claims.bulkEdit.providerGroup',
              'Provider and group'
            )}
            keys={providerKeys}
            cols={2}
            providers={providers}
            editComponentsMap={editComponentsMap}
          />
          <ClaimDetailSection
            index={row.id}
            title={translate(
              'pages:claims.bulkEdit.claimDetails',
              'Claim details'
            )}
            keys={detailKeys}
            cols={3}
            editComponentsMap={editComponentsMap}
          />
          <ClaimDetailSection
            index={row.id}
            title={translate('pages:claims.bulkEdit.notes', 'Notes')}
            keys={notesKeys}
            cols={2}
            editComponentsMap={editComponentsMap}
          />
          <ClaimCommentSection
            index={row.id}
            title={translate('pages:claims.comments.title', 'Comments')}
            keys={claimCommentSectionKeys}
            cols={1}
            editComponentsMap={editComponentsMap}
            type="bulkEdit"
          />
          <ClaimTariffSection
            index={row.id}
            title={translate(
              'pages:claims.bulkEdit.tariff',
              'Tariff and service codes'
            )}
            cols={1}
            keys={tariffSectionKeys}
            editComponentsMap={editComponentsMap}
            type="bulkEdit"
          />
        </Stack>
      </ClaimFormProvider>
      <Divider />
      <Group justify="flex-end">
        <Button variant="default" onClick={close} disabled={loading}>
          {translate('common.cancel', 'Cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          loading={loading}
          disabled={!hasChangedValues}
        >
          {translate('pages:claims.bulkEdit.apply', 'Apply to selected claims')}
        </Button>
      </Group>
    </Stack>
  );
};
